import { byId, isAr, formatCurrency, formatNumber, attachCopyButton, setupLiveCalculation, detectUserCurrency } from './calc-common.js';
import Big from 'big.js';
import { getInvoiceTafgeet } from './invoice-currencies.js';

const STORAGE_KEY = 'kazakan_ot_calc_v2';

export function calculateOvertime({
  wageMode = 'monthly',
  monthlyWage = 0,
  basic = 0,
  housing = 0,
  transport = 0,
  other = 0,
  otBasis = 'actual',
  hourlyWage = 0,
  monthlyStandardHours = 240,
  overtimeHours = 0,
  regularHours = 0,
  multiplier = 1.5
}) {
  const b = Big(basic || monthlyWage || 0);
  const h = Big(housing || 0);
  const t = Big(transport || 0);
  const o = Big(other || 0);
  const grossWage = b.plus(h).plus(t).plus(o);

  let effectiveWage = Big(0);
  if (otBasis === 'actual') {
    effectiveWage = grossWage.gt(0) ? grossWage : b;
  } else {
    effectiveWage = b;
  }

  let baseHourlyRate = Big(0);
  if (wageMode === 'monthly') {
    const stdHours = Big(monthlyStandardHours).gt(0) ? Big(monthlyStandardHours) : Big(240);
    baseHourlyRate = effectiveWage.gt(0) ? effectiveWage.div(stdHours) : Big(0);
  } else {
    baseHourlyRate = Big(hourlyWage || 0);
  }

  const mult = Big(multiplier || 1.5);
  const otRate = baseHourlyRate.times(mult);
  const otPay = Big(overtimeHours || 0).times(otRate);
  const regularPay = Big(regularHours || 0).times(baseHourlyRate);
  const totalPay = regularPay.plus(otPay);

  let pctOfSalary = Big(0);
  if (effectiveWage.gt(0)) {
    pctOfSalary = otPay.div(effectiveWage).times(100);
  }

  return {
    grossWage: Number(grossWage.toFixed(2)),
    effectiveWage: Number(effectiveWage.toFixed(2)),
    baseHourlyRate: Number(baseHourlyRate.toFixed(2)),
    otRate: Number(otRate.toFixed(2)),
    otPay: Number(otPay.toFixed(2)),
    regularPay: Number(regularPay.toFixed(2)),
    totalPay: Number(totalPay.toFixed(2)),
    pctOfSalary: Number(pctOfSalary.toFixed(1))
  };
}

let multiDayLogs = [
  { id: 1, label: isAr ? 'يوم الأحد' : 'Sunday', regH: 8, otH: 2, mult: 1.5 },
  { id: 2, label: isAr ? 'يوم الإثنين' : 'Monday', regH: 8, otH: 1.5, mult: 1.5 },
  { id: 3, label: isAr ? 'عطلة الجمعة' : 'Friday Holiday', regH: 0, otH: 5, mult: 2.0 }
];
let nextLogId = 4;

export function initOvertimeCalculator() {
  const container = byId('overtime-calculator-app');
  if (!container) return;

  // 1. Wage Input Mode (Monthly vs Hourly)
  const wageModeSelect = byId('ot-wage-mode');
  const monthlyGroup = byId('ot-monthly-group');
  const hourlyGroup = byId('ot-hourly-group');

  function updateWageModeUI() {
    const mode = wageModeSelect?.value || 'monthly';
    if (monthlyGroup) monthlyGroup.style.display = mode === 'monthly' ? 'block' : 'none';
    if (hourlyGroup) hourlyGroup.style.display = mode === 'hourly' ? 'block' : 'none';
  }
  if (wageModeSelect) {
    wageModeSelect.addEventListener('change', () => {
      updateWageModeUI();
      runOtCalc();
      saveDraft();
    });
  }

  // 2. Standard Hours Presets Chips
  container.querySelectorAll('.ot-hours-preset').forEach((btn) => {
    btn.addEventListener('click', () => {
      container.querySelectorAll('.ot-hours-preset').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      const hrs = btn.dataset.hours;
      if (hrs !== 'custom' && byId('ot-monthly-standard-hours')) {
        byId('ot-monthly-standard-hours').value = hrs;
      }
      runOtCalc();
      saveDraft();
    });
  });

  // 3. Multiplier Segmented Tabs
  container.querySelectorAll('.ot-mult-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      container.querySelectorAll('.ot-mult-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      const multVal = btn.dataset.mult;
      if (byId('ot-multiplier')) {
        byId('ot-multiplier').value = multVal;
      }
      runOtCalc();
      saveDraft();
    });
  });

  // 4. Overtime Calculation Modes: 'shift' vs 'direct' vs 'multiday'
  container.querySelectorAll('.ot-calc-mode-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      container.querySelectorAll('.ot-calc-mode-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      const mode = btn.dataset.mode;
      if (byId('ot-calc-mode')) byId('ot-calc-mode').value = mode;

      const shiftSec = byId('ot-shift-builder-sec');
      const directSec = byId('ot-direct-hours-sec');
      const multiSec = byId('ot-multiday-sec');

      if (shiftSec) shiftSec.style.display = mode === 'shift' ? 'block' : 'none';
      if (directSec) directSec.style.display = mode === 'direct' ? 'block' : 'none';
      if (multiSec) multiSec.style.display = mode === 'multiday' ? 'block' : 'none';

      runOtCalc();
      saveDraft();
    });
  });

  // 5. Shift time calculator helper (start time -> end time -> break -> duration)
  const shiftStart = byId('ot-shift-start');
  const shiftEnd = byId('ot-shift-end');
  const shiftBreak = byId('ot-shift-break');
  const shiftStandard = byId('ot-shift-standard-hours');

  function updateHoursFromShift() {
    if (!shiftStart?.value || !shiftEnd?.value) return;
    const [startH, startM] = shiftStart.value.split(':').map(Number);
    const [endH, endM] = shiftEnd.value.split(':').map(Number);

    let startMinutes = startH * 60 + startM;
    let endMinutes = endH * 60 + endM;

    // Overnight shift crossing midnight
    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60;
    }

    const breakMins = parseFloat(shiftBreak?.value) || 0;
    const workedMinutes = Math.max(0, endMinutes - startMinutes - breakMins);
    const workedHours = workedMinutes / 60;

    const stdHours = parseFloat(shiftStandard?.value) || 8;
    const regHours = Math.min(workedHours, stdHours);
    const otHours = Math.max(0, workedHours - stdHours);

    if (byId('ot-regular-hours')) byId('ot-regular-hours').value = regHours.toFixed(1);
    if (byId('ot-hours')) byId('ot-hours').value = otHours.toFixed(1);

    const shiftDurationBadge = byId('ot-shift-total-badge');
    if (shiftDurationBadge) {
      shiftDurationBadge.textContent = `${workedHours.toFixed(1)} ${isAr ? 'ساعة عمل' : 'hrs'}`;
    }

    runOtCalc();
    saveDraft();
  }

  [shiftStart, shiftEnd, shiftBreak, shiftStandard].forEach((el) => {
    if (el) el.addEventListener('input', updateHoursFromShift);
  });

  // 6. Multi-Day Log table interaction
  const addDayBtn = byId('ot-add-day-btn');
  if (addDayBtn) {
    addDayBtn.addEventListener('click', () => {
      multiDayLogs.push({
        id: nextLogId++,
        label: `${isAr ? 'يوم عمل' : 'Workday'} #${multiDayLogs.length + 1}`,
        regH: 8,
        otH: 2,
        mult: 1.5
      });
      renderMultiDayTable();
      runOtCalc();
      saveDraft();
    });
  }

  // 7. Inputs live trigger
  setupLiveCalculation(
    [
      'ot-wage-mode',
      'ot-basic-salary',
      'ot-housing-allowance',
      'ot-transport-allowance',
      'ot-other-allowance',
      'ot-basis',
      'ot-monthly-standard-hours',
      'ot-hourly-wage',
      'ot-multiplier',
      'ot-hours',
      'ot-regular-hours',
      'ot-direct-ot-hours',
      'ot-direct-days',
      'ot-currency'
    ],
    () => {
      runOtCalc();
      saveDraft();
    }
  );

  // 8. Copy Summary Button
  attachCopyButton(byId('ot-copy-btn'), () => {
    const otPay = byId('ot-pay-result')?.textContent || '';
    const totalPay = byId('ot-total-result')?.textContent || '';
    const baseRate = byId('ot-base-rate-val')?.textContent || '';
    const otRate = byId('ot-rate-val')?.textContent || '';
    const hrs = byId('ot-summary-hours')?.textContent || '';
    const currency = byId('ot-currency')?.value || 'SAR';

    return isAr
      ? `📋 ملخص مستحقات العمل الإضافي (الأوفر تايم):\n• أجر ساعات الإضافي المستحق: ${otPay}\n• ساعات الإضافي المؤداة: ${hrs}\n• سعر ساعة الإضافي (150%): ${otRate}\n• أجر الساعة العادية: ${baseRate}\n• إجمالي المستحقات الإجمالية: ${totalPay}`
      : `📋 Overtime Compensation Summary:\n• Overtime Payout: ${otPay}\n• Overtime Hours: ${hrs}\n• Overtime Hourly Rate: ${otRate}\n• Base Hourly Rate: ${baseRate}\n• Total Payout: ${totalPay}`;
  });

  // 9. Print Report Button
  const printBtn = byId('ot-print-btn');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      window.print();
    });
  }

  // 10. Reset Draft Button
  const resetBtn = byId('ot-reset-btn');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      if (
        confirm(
          isAr
            ? 'هل تريد استعادة الإعدادات الافتراضية ومسح المسودة؟'
            : 'Reset overtime calculator to defaults?'
        )
      ) {
        localStorage.removeItem(STORAGE_KEY);
        location.reload();
      }
    });
  }

  loadDraft();
  updateWageModeUI();
  renderMultiDayTable();
  runOtCalc();
}

function renderMultiDayTable() {
  const tbody = byId('ot-multiday-tbody');
  if (!tbody) return;

  tbody.innerHTML = multiDayLogs
    .map(
      (day, idx) => `
    <tr data-id="${day.id}">
      <td>
        <input type="text" class="kz-input ot-day-label" value="${day.label}" style="min-height:36px; padding:4px 8px; font-size:0.8125rem;">
      </td>
      <td style="width:75px; text-align:center;">
        <input type="number" step="0.5" min="0" class="kz-input ot-day-reg" value="${day.regH}" style="min-height:36px; padding:4px; text-align:center; font-size:0.8125rem;">
      </td>
      <td style="width:75px; text-align:center;">
        <input type="number" step="0.5" min="0" class="kz-input ot-day-ot" value="${day.otH}" style="min-height:36px; padding:4px; text-align:center; font-size:0.8125rem; font-weight:700; color:var(--brand-primary);">
      </td>
      <td style="width:115px;">
        <select class="kz-input ot-day-mult" style="min-height:36px; padding:4px 6px; font-size:0.75rem;">
          <option value="1.5" ${day.mult === 1.5 ? 'selected' : ''}>1.5x (${isAr ? 'عادي' : 'Normal'})</option>
          <option value="2.0" ${day.mult === 2.0 ? 'selected' : ''}>2.0x (${isAr ? 'عطلة' : 'Holiday'})</option>
          <option value="1.25" ${day.mult === 1.25 ? 'selected' : ''}>1.25x</option>
        </select>
      </td>
      <td style="width:40px; text-align:center;">
        <button type="button" class="ot-btn-del" title="${isAr ? 'حذف الوردية' : 'Delete'}" aria-label="Delete">✕</button>
      </td>
    </tr>
  `
    )
    .join('');

  // Row listeners
  tbody.querySelectorAll('.ot-day-label').forEach((inp, i) => {
    inp.addEventListener('input', (e) => {
      multiDayLogs[i].label = e.target.value;
      saveDraft();
    });
  });
  tbody.querySelectorAll('.ot-day-reg').forEach((inp, i) => {
    inp.addEventListener('input', (e) => {
      multiDayLogs[i].regH = parseFloat(e.target.value) || 0;
      runOtCalc();
      saveDraft();
    });
  });
  tbody.querySelectorAll('.ot-day-ot').forEach((inp, i) => {
    inp.addEventListener('input', (e) => {
      multiDayLogs[i].otH = parseFloat(e.target.value) || 0;
      runOtCalc();
      saveDraft();
    });
  });
  tbody.querySelectorAll('.ot-day-mult').forEach((sel, i) => {
    sel.addEventListener('change', (e) => {
      multiDayLogs[i].mult = parseFloat(e.target.value) || 1.5;
      runOtCalc();
      saveDraft();
    });
  });
  tbody.querySelectorAll('.ot-btn-del').forEach((btn, i) => {
    btn.addEventListener('click', () => {
      if (multiDayLogs.length <= 1) return;
      multiDayLogs.splice(i, 1);
      renderMultiDayTable();
      runOtCalc();
      saveDraft();
    });
  });
}

function runOtCalc() {
  const wageMode = byId('ot-wage-mode')?.value || 'monthly';
  const basic = parseFloat(byId('ot-basic-salary')?.value) || 0;
  const housing = parseFloat(byId('ot-housing-allowance')?.value) || 0;
  const transport = parseFloat(byId('ot-transport-allowance')?.value) || 0;
  const other = parseFloat(byId('ot-other-allowance')?.value) || 0;
  const otBasis = byId('ot-basis')?.value || 'actual';
  const hourlyWage = parseFloat(byId('ot-hourly-wage')?.value) || 0;
  const monthlyStandardHours = parseFloat(byId('ot-monthly-standard-hours')?.value) || 240;
  const multiplier = parseFloat(byId('ot-multiplier')?.value) || 1.5;
  const currency = byId('ot-currency')?.value || 'SAR';
  const calcMode = byId('ot-calc-mode')?.value || 'shift';

  let totalOtHours = 0;
  let totalRegHours = 0;
  let totalOtPay = 0;
  let totalRegPay = 0;

  // Base calculation for single day / reference rate
  const res = calculateOvertime({
    wageMode,
    basic,
    housing,
    transport,
    other,
    otBasis,
    hourlyWage,
    monthlyStandardHours,
    overtimeHours: 1,
    regularHours: 8,
    multiplier
  });

  if (calcMode === 'shift') {
    totalRegHours = parseFloat(byId('ot-regular-hours')?.value) || 0;
    totalOtHours = parseFloat(byId('ot-hours')?.value) || 0;
    totalOtPay = totalOtHours * res.otRate;
    totalRegPay = totalRegHours * res.baseHourlyRate;
  } else if (calcMode === 'direct') {
    totalOtHours = parseFloat(byId('ot-direct-ot-hours')?.value) || 0;
    const directDays = parseFloat(byId('ot-direct-days')?.value) || 22;
    totalRegHours = directDays * 8;
    totalOtPay = totalOtHours * res.otRate;
    totalRegPay = totalRegHours * res.baseHourlyRate;
  } else if (calcMode === 'multiday') {
    totalRegHours = 0;
    totalOtHours = 0;
    totalOtPay = 0;
    multiDayLogs.forEach((day) => {
      totalRegHours += day.regH;
      totalOtHours += day.otH;
      const dayOtRate = res.baseHourlyRate * day.mult;
      totalOtPay += day.otH * dayOtRate;
      totalRegPay += day.regH * res.baseHourlyRate;
    });
  }

  const grandTotal = totalRegPay + totalOtPay;

  // Update Hero Result Box
  if (byId('ot-pay-result')) {
    byId('ot-pay-result').textContent = formatCurrency(totalOtPay, currency);
  }
  if (byId('ot-total-result')) {
    byId('ot-total-result').textContent = formatCurrency(grandTotal, currency);
  }

  // Update Percentage of wage badge
  const pctBadge = byId('ot-pct-badge');
  if (pctBadge) {
    if (res.effectiveWage > 0) {
      const pct = ((totalOtPay / res.effectiveWage) * 100).toFixed(1);
      pctBadge.textContent = `+${pct}% ${isAr ? 'إضافي على الراتب' : 'of monthly wage'}`;
      pctBadge.style.display = 'inline-block';
    } else {
      pctBadge.style.display = 'none';
    }
  }

  // Update breakdown rows
  if (byId('ot-effective-wage-val')) {
    byId('ot-effective-wage-val').textContent = formatCurrency(res.effectiveWage, currency);
  }
  if (byId('ot-base-rate-val')) {
    byId('ot-base-rate-val').textContent = formatCurrency(res.baseHourlyRate, `${currency}/h`);
  }
  if (byId('ot-rate-val')) {
    byId('ot-rate-val').textContent = formatCurrency(res.otRate, `${currency}/h`);
  }
  if (byId('ot-summary-hours')) {
    byId('ot-summary-hours').textContent = `${totalOtHours.toFixed(1)} ${isAr ? 'ساعة إضافية' : 'hrs'}`;
  }
  if (byId('ot-reg-pay-val')) {
    byId('ot-reg-pay-val').textContent = formatCurrency(totalRegPay, currency);
  }

  // Tafgeet (amount in words)
  const tafgeetEl = byId('ot-tafgeet-val');
  if (tafgeetEl) {
    tafgeetEl.textContent = getInvoiceTafgeet(totalOtPay.toFixed(2), currency);
  }
}

function saveDraft() {
  try {
    const fields = [
      'ot-wage-mode',
      'ot-basic-salary',
      'ot-housing-allowance',
      'ot-transport-allowance',
      'ot-other-allowance',
      'ot-basis',
      'ot-monthly-standard-hours',
      'ot-hourly-wage',
      'ot-multiplier',
      'ot-calc-mode',
      'ot-shift-start',
      'ot-shift-end',
      'ot-shift-break',
      'ot-shift-standard-hours',
      'ot-hours',
      'ot-regular-hours',
      'ot-direct-ot-hours',
      'ot-direct-days',
      'ot-currency'
    ];
    const data = { multiDayLogs };
    fields.forEach((f) => {
      const el = byId(f);
      if (el) data[f] = el.value;
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (err) {
    console.warn('Could not save overtime draft', err);
  }
}

function loadDraft() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      if (byId('ot-currency')) {
        byId('ot-currency').value = detectUserCurrency('SAR');
      }
      return;
    }
    const data = JSON.parse(saved);
    if (Array.isArray(data.multiDayLogs) && data.multiDayLogs.length > 0) {
      multiDayLogs = data.multiDayLogs;
      nextLogId = Math.max(...multiDayLogs.map((d) => d.id), 0) + 1;
    }
    const fields = [
      'ot-wage-mode',
      'ot-basic-salary',
      'ot-housing-allowance',
      'ot-transport-allowance',
      'ot-other-allowance',
      'ot-basis',
      'ot-monthly-standard-hours',
      'ot-hourly-wage',
      'ot-multiplier',
      'ot-calc-mode',
      'ot-shift-start',
      'ot-shift-end',
      'ot-shift-break',
      'ot-shift-standard-hours',
      'ot-hours',
      'ot-regular-hours',
      'ot-direct-ot-hours',
      'ot-direct-days',
      'ot-currency'
    ];
    fields.forEach((f) => {
      if (data[f] !== undefined && byId(f)) {
        byId(f).value = data[f];
      }
    });

    // Sync button states
    if (data['ot-monthly-standard-hours']) {
      document.querySelectorAll('.ot-hours-preset').forEach((b) => {
        b.classList.toggle('active', b.dataset.hours === data['ot-monthly-standard-hours']);
      });
    }
    if (data['ot-multiplier']) {
      document.querySelectorAll('.ot-mult-btn').forEach((b) => {
        b.classList.toggle('active', b.dataset.mult === data['ot-multiplier']);
      });
    }
    if (data['ot-calc-mode']) {
      document.querySelectorAll('.ot-calc-mode-btn').forEach((b) => {
        b.classList.toggle('active', b.dataset.mode === data['ot-calc-mode']);
      });
      const mode = data['ot-calc-mode'];
      const shiftSec = byId('ot-shift-builder-sec');
      const directSec = byId('ot-direct-hours-sec');
      const multiSec = byId('ot-multiday-sec');
      if (shiftSec) shiftSec.style.display = mode === 'shift' ? 'block' : 'none';
      if (directSec) directSec.style.display = mode === 'direct' ? 'block' : 'none';
      if (multiSec) multiSec.style.display = mode === 'multiday' ? 'block' : 'none';
    }
  } catch (err) {
    console.warn('Could not load overtime draft', err);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOvertimeCalculator);
  } else {
    initOvertimeCalculator();
  }
}
